/**
 * Clean up email newsletter content by removing boilerplate text
 */
export function cleanEmailContent(content) {
  if (!content) return '';

  let cleaned = content;

  // Remove "View this email in your browser" and variations
  cleaned = cleaned.replace(/View this email in your browser[^\n]*\n?/gi, '');
  cleaned = cleaned.replace(/View this email in your browser \(\)/gi, '');

  // Remove standalone ppiweb.io URLs (but not when part of a link)
  cleaned = cleaned.replace(/^https?:\/\/ppiweb\.io\s*$/gm, '');
  cleaned = cleaned.replace(/\nhttps?:\/\/ppiweb\.io\s*\n/g, '\n');

  // Replace personalized greetings with generic "Hi,"
  // Handles: "Hi John,", "Hi *|FNAME|*,", "Hi , ", "Dear Tim,", "Dear *|FNAME|*," etc.
  cleaned = cleaned.replace(/^(?:Hi|Dear)\s+[^\n,]*,\s*/i, 'Hi,\n\n');
  cleaned = cleaned.replace(/^(?:Hi|Dear)\s*,\s*/i, 'Hi,\n\n');
  cleaned = cleaned.replace(/^Hi,\s*/i, 'Hi,\n\n');

  // Clean up leftover merge tag artifacts
  cleaned = cleaned.replace(/,\s*,\s*/g, ' ');
  cleaned = cleaned.replace(/^,\s+/gm, '');

  // Remove promotional/summit sections enclosed between horizontal rule lines (----)
  // These are typically separated by lines of dashes (4+ dashes) and contain registration CTAs
  cleaned = cleaned.replace(/-{4,}[\s\S]*?-{4,}/g, '');

  // Remove Tim Autrey signature block and everything after it
  // Matches "– Tim" or "-- Tim" or standalone "Tim Autrey" signature lines and truncates
  cleaned = cleaned.replace(/\n[–—-]{1,2}\s*Tim\b[\s\S]*$/i, '');

  // Remove signature block starting with "Tim Autrey" (with optional markdown formatting)
  cleaned = cleaned.replace(/\n\**Tim Autrey\**[\s\S]*$/i, '');

  // Remove "Founder- PPI" block and everything after
  cleaned = cleaned.replace(/\n\**Founder-?\s*PPI[\s\S]*$/i, '');

  // Remove social sharing links block (Facebook, Twitter, LinkedIn, Forward)
  cleaned = cleaned.replace(/\n*https?:\/\/(?:www\.)?facebook\.com\/sharer[\s\S]*$/i, '');
  cleaned = cleaned.replace(/\n*https?:\/\/(?:www\.)?twitter\.com\/intent[\s\S]*$/i, '');
  cleaned = cleaned.replace(/\n*Share \(http[\s\S]*$/i, '');

  // Remove "You can update your preferences" and unsubscribe text
  cleaned = cleaned.replace(/\n*You can update your preferences[^\n]*[\s\S]*$/i, '');

  // Remove common email end matter patterns
  // Unsubscribe links and related text
  cleaned = cleaned.replace(/unsubscribe from this list[^\n]*\n?/gi, '');
  cleaned = cleaned.replace(/update subscription preferences[^\n]*\n?/gi, '');
  cleaned = cleaned.replace(/\*\|UNSUB\|\*[^\n]*\n?/g, '');
  cleaned = cleaned.replace(/\*\|UPDATE_PROFILE\|\*[^\n]*\n?/g, '');

  // Mailchimp footer patterns
  cleaned = cleaned.replace(/Copyright ©[^*\n]*\n?/gi, '');
  cleaned = cleaned.replace(/All rights reserved\.[^\n]*\n?/gi, '');
  cleaned = cleaned.replace(/Our mailing address is:[^\n]*\n?/gi, '');
  cleaned = cleaned.replace(/Want to change how you receive these emails\?[^\n]*\n?/gi, '');

  // Remove Mailchimp merge tags that might appear
  cleaned = cleaned.replace(/\*\|[A-Z_]+\|\*/g, '');

  // Convert email-style "link text (url)" patterns into markdown links [text](url)
  // Handles: "Some Text (https://...)" and "Some Text (mailto:...)"
  // Also handles: "text (non-url) (https://...)" where parenthetical text precedes the URL
  cleaned = cleaned.replace(
    /(?:^|(?<=\n))([^\n]*?(?:\([^)]*\)\s*)?[^\n(]*?)\s*\((https?:\/\/[^)]+)\)/gm,
    (match, text, url) => {
      const trimmed = text.trim();
      if (!trimmed) return match;
      return `[${trimmed}](${url})`;
    }
  );
  cleaned = cleaned.replace(
    /(?:^|(?<=\n))([^\n]*?)\s*\((mailto:[^)]+)\)/gm,
    (match, text, url) => {
      const trimmed = text.trim();
      if (!trimmed) return match;
      return `[${trimmed}](${url})`;
    }
  );

  // Clean up standalone bare URLs that aren't already in markdown link syntax
  // Turns "https://example.com" on its own line into a clickable link
  cleaned = cleaned.replace(
    /(?<!\]\()(?<!\()(?:^|\n)(https?:\/\/[^\s)]+)/gm,
    (match, url) => match.replace(url, `[${url}](${url})`)
  );

  // Clean up multiple blank lines
  cleaned = cleaned.replace(/\n{3,}/g, '\n\n');

  // Trim whitespace
  cleaned = cleaned.trim();

  return cleaned;
}

/**
 * Parse markdown-like content into HTML
 * Supports: **bold**, *italic*, [link](url), ![alt](url), double line breaks
 */
export function parseContent(content) {
  if (!content) return '';

  // First clean up email boilerplate
  let html = cleanEmailContent(content);

  // Escape HTML entities first
  html = html
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

  // Images: ![alt text](url) - must be processed before links
  html = html.replace(
    /!\[([^\]]*)\]\(([^)]+)\)/g,
    '<img src="$2" alt="$1" loading="lazy" class="content-image" />'
  );

  // Links: [link text](url)
  html = html.replace(
    /\[([^\]]+)\]\(([^)]+)\)/g,
    '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>'
  );

  // Bold: **text**
  html = html.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');

  // Italic: *text*
  html = html.replace(/\*([^*]+)\*/g, '<em>$1</em>');

  // Double line breaks → paragraphs
  const paragraphs = html.split(/\n\n+/);
  html = paragraphs
    .map((p) => p.trim())
    .filter((p) => p)
    .map((p) => `<p>${p.replace(/\n/g, '<br />')}</p>`)
    .join('');

  return html;
}

/**
 * Format a Firestore timestamp to a readable date
 */
export function formatDate(timestamp) {
  if (!timestamp) return '';

  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);

  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

/**
 * Calculate estimated reading time
 */
export function getReadingTime(content) {
  if (!content) return '1 min read';

  const wordsPerMinute = 200;
  const words = content.trim().split(/\s+/).length;
  const minutes = Math.ceil(words / wordsPerMinute);

  return `${minutes} min read`;
}
